<script>
import { defineAsyncComponent } from "@common/defineAsyncComponent";
import { componentStore } from "@common/componentStore/componentStore";
import { actionStore } from "@common/actionStore";
import { library } from "@common/library/library";
import { SkeletonComponent } from "@syncfusion/ej2-vue-notifications";
import { recordStore } from "@common/recordStore";

const runOnceActions = {
    // Put actions in only
};
let uniqueID = library.getUniqueID();
let generateIdFnc = function (type, itemIndex = 0, propIndex = 0, notUnique = false) {
    const cId = this.cId.replace(" ", /_/g).toLowerCase();
    let templateId = `itemTemplate_${cId}_${itemIndex}_${propIndex}_${type}`;
    if (notUnique) return templateId;
    return `${templateId}_${uniqueID}`;
};
let localComponents = {};

let isExistingTemplate = (itemTemplate) => {
    let output = false;
    for (let templateId in localComponents) {
        if (localComponents[templateId] === itemTemplate) {
            output = templateId;
            break;
        }
    }
    return output;
};

let createComp = function (template, type, itemIndex, titleIndex) {
    let tmpTemplate = `<div>${template}</div>`;
    // Don't create new component when one with exact same template already exists
    let existing = isExistingTemplate(tmpTemplate);
    if (existing) return existing;
    let iTemplateCmpId = !this.config?.groupItemsBy ? generateIdFnc.call(this, type, itemIndex, titleIndex, true) : generateIdFnc.call(this, type, itemIndex, titleIndex);
    let vResults = library.validateHTML(template);
    if (!vResults?.valid) {
        let errors = vResults?.results[0]?.messages;
        errors = errors.map((err) => err.message
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;'));
        tmpTemplate = `<div>
            (Invalid Template)<br/>
            ${errors.map((message) => `${message}<br/>`)}
        </div>`;
    }
    let component = this;
    // let recordStore = this.recordStore;
    vueApp.component(iTemplateCmpId, {
        template: tmpTemplate,
        data() {
            return {
                templateData: {}, // A place to put custom, local, template specific settings
                // component: component,
            };
        },
        props: {
            hovered: {
                type: Boolean,
                default: false
            },
            targeted: {
                type: Boolean,
                default: false
            },
            record: {
                type: Object,
                default() {
                    return {};
                }
            },
            itemRecords: {
                type: Array,
                default() {
                    return [];
                }
            },
            index: {}
        },
        computed: {
            moment() {
                return library.getMomentObj();
            },
            actionStore() {
                return actionStore;
            },
            records() {
                return recordStore().records;
            },
            // These will be available fields to use in the itemList headerTemplate
            // example: records.vol[volId].VOL_FIRST_NAME
            // example: records.user.CLIENT_NAME
            // example: records.opp[oppId]['CA#Application_Questions/Region_0']
            volId() {
                return component.contextVolId;
            },
            user() {
                return component.user;
            },
            oppId() {
                return component.contextOppId || component.oppId;
            },
            orgId() {
                return component.contextOrgId || component.orgId;
            },
            aeId() {
                return component.contextAeId || component.aeId;
            },
            entryPointId() {
                return component.contextEntryPointId || component.entryPointId;
            },
            affId() {
                return component.contextAffId || component.affId;
            },
            schId() {
                return component.contextSchId || component.schId;
            },
            lbeId() {
                return component.contextLbeId || component.lbeId;
            },
            datasetId() {
                return component.contextDatasetId || component.datasetId;
            }
        },
        methods: {
            trimHTML(str) {
                return library.trimHTML(str);
            },
            dateDetails(dateStr, formatStr) {
                let momentDate = library.getMoment(dateStr, formatStr);
                if (!momentDate.isValid()) {
                    momentDate = library.getMoment(dateStr, 'D MMM YYYY');
                }
                if (!momentDate.isValid()) {
                    momentDate = library.getMoment(dateStr, 'MM/DD/YYYY');
                }
                return {
                    D: momentDate.format('D'),
                    MMM: momentDate.format('MMM'),
                    dddd: momentDate.format('dddd'),
                };
            },
            toggleDelay(time, property) {
                this.templateData[property] = !this.templateData[property];
                setTimeout(() => {
                    this.templateData[property] = !this.templateData[property];
                }, parseInt(time, 10));
            }
        }
    });
    // TODO also destroy these components when the itemList is destroyed
    localComponents[iTemplateCmpId] = tmpTemplate;
    return iTemplateCmpId;
};

const setIncludeWhenLogicProps = (pObjLabel) => ({
    type: 'logicEditor',
    otherProps: {
        hideStandardRecordTypes: true,
        recordTypes: [
            { text: 'Record', value: 'object2' },
            { text: pObjLabel, value: 'object1' },
            { text: 'Previous Item', value: 'object3' },
            { text: 'Next Item', value: 'object4' },
        ],
        otherColumns: [
            {
                field: 'complete',
                label: 'Item: complete',
                type: 'boolean',
                category: 'Item Props',
                operators: 'booleanOperators'
            }, {
                field: 'enable',
                label: 'Item: enable',
                type: 'boolean',
                category: 'Item Props',
                operators: 'booleanOperators'
            }, {
                field: 'include',
                label: 'Item: include',
                type: 'boolean',
                category: 'Item Props',
                operators: 'booleanOperators'
            }
        ],
        defaultRecordType: 'object2'
    },
    default: []
});

const fontList = [
    { text: 'Proxima Nova', value: 'proxima-nova' },
    { text: 'Open Sans', value: 'Open Sans' },
    { text: 'Source Serif Pro', value: 'Source Serif Pro' },
    { text: 'Schoolbook', value: 'Schoolbook' },
    { text: 'Arial', value: 'Arial' },
    { text: 'Times New Roman', value: 'Times New Roman' },
    { text: 'Courier New', value: 'Courier New' },
    { text: 'Comic Sans MS', value: 'Comic Sans MS' },
    { text: 'Brush Script MT', value: 'Brush Script MT' },
];

const fontWeights = [
    { text: 'Light 300', value: '300' },
    { text: 'Regular 400', value: '400' },
    { text: 'Semi-bold 600', value: '600' },
    { text: 'Bold 700', value: '700' },
    { text: 'Extra-bold 800', value: '800' },
];

const fieldsObj = {
    text: 'text',
    value: 'value'
};

const editorSettings = {
    generalSettingsPanel: {
        type: 'panel',
        label: 'General Settings',
        id: 'generalSettingsPanel',
        fields: [
            'datasetIds',
            'parentRecordType',
            'sortBy',
            'sortField',
            'sortDirection',
            'noRecordsMessage',
            'allowDragAndDrop',
            'hideWhenNoItems',
            'itemListHeight',
            'visibleItemCount',
            'showPaginationInHeader',
            'showPaginationAtTop',
            'itemIconType',
            'itemIconSize',
            'avatarContactCardId',
            'iconStyles'
        ]
    },
    headerSettings: {
        type: 'panel',
        label: 'Header Settings',
        id: 'headerSettings',
        fields: [
            'header',
            'hideListHeader',
            'headerStyles'
        ]
    },
    groupSettings: {
        type: 'panel',
        label: 'Group Settings',
        id: 'groupSettings',
        fields: [
            'groupItemsBy',
            'expandGroupDefault',
            'customGroupHeaders',
            'txtGroupHeading',
        ]
    },
    filterSettings: {
        type: 'panel',
        label: 'Filter Settings',
        id: 'filterSettings',
        fields: [
            'enableFilter',
            'filterOptionsList',
            'additionalConfig'
        ]
    },
    itemListPanel: {
        type: 'panel',
        label: 'Item Settings',
        id: 'itemListPanel',
        fields: [
            'itemsListSource',
            'itemList'
        ]
    },
    enableFilter: {
        label: 'Enable Search Filter',
        type: 'boolean',
        id: 'enableFilter',
        helpText: 'Display a configurable search filter at the top of the item list.'
    },
    filterOptionsList: {
        label: 'Filter Settings',
        type: 'array',
        id: 'filterSettings',
        helpText: 'Settings for the search filter',
        default: [],
        itemConfig: {
            itemType: 'object',
            primaryKey: 'filterKey',
            permissions: {
                canMove: true,
                canDelete: true,
                allowAddItem: true
            },
            keys: {
                filterKey: {
                    type: 'string',
                    label: 'Key',
                    id: 'filterKey',
                    helpText: 'The key in your record object you want this filter to query.'
                },
                label: {
                    type: 'string',
                    label: 'Label',
                    id: 'label',
                    helpText: 'The label that is displayed in the filter list.'
                },
                type: {
                    type: 'dropdown',
                    label: 'Type',
                    id: 'type',
                    items: [
                        { text: 'String', value: 'string' },
                        { text: 'Dropdown', value: 'dropdown' },
                        { text: 'Date', value: 'date' },
                    ],
                    fields: {
                        text: 'text',
                        value: 'value'
                    },
                    helpText: 'Type of control to use for the filter.'
                }
            },
            newItem: {
                filterKey: 'someProperty',
                label: 'New Filter',
                type: 'string'
            }
        },
        logic: [
            {
                record: 'self',
                criteriaRecord: 'component',
                prop: 'display',
                valueType: 'criteria',
                criteria: "this.controls.enableFilter.value"
            }
        ]
    },
    additionalConfig: {
        label: 'Filter Additional Config',
        type: 'object',
        id: 'additionalConfig',
        helpText: 'Condensed filter additional config.',
        default: {},
        properties: {
            showResultsAfterFiltersApplied: {
                type: 'boolean',
                label: 'Hide Results Until Filter is Applied',
                id: 'showResultsAfterFiltersApplied',
                helpText: 'Do not show item list items until a filter is applied.'
            }
        }
    },
    groupItemsBy: {
        id: 'groupItemsBy',
        type: 'dropdowntree',
        itemsSource: 'configStore.allFieldOptions()',
        allowFiltering: true,
        filterType: 'Contains',
        label: 'Group Items By',
    },
    expandGroupDefault: {
        type: 'boolean',
        id: 'expandGroupDefault',
        label: 'Allow Expansion on Single-Item Groups',
        default: true,
        helpText: 'Turn this off to disable the expand option for groups that contain only one item.',
        logic: [
            {
                record: 'self',
                prop: 'display',
                valueType: 'criteria',
                criteria: "this.controls.groupItemsBy.value.length"
            }
        ],
    },
    customGroupHeaders: {
        type: 'boolean',
        id: 'customGroupHeaders',
        label: 'Use Custom Group Header',
        default: false,
        logic: [
            {
                record: 'self',
                prop: 'display',
                valueType: 'criteria',
                criteria: "this.controls.groupItemsBy.value.length"
            }
        ],
    },
    txtGroupHeading: {
        type: 'text',
        id: 'txtGroupHeading',
        label: 'HTML Group Heading',
        showExpandMinimizeBtns: true,
        default: '<p>Group Header</p>',
        logic: [
            {
                record: 'self',
                prop: 'display',
                valueType: 'criteria',
                criteria: "this.controls.customGroupHeaders.value"
            }
        ],
    },
    allowDragAndDrop: {
        type: 'boolean',
        label: 'Allow Drag and Drop',
        helpText: 'Allow drag and drop.',
        id: 'allowDragAndDrop',
        default: false
    },
    header: {
        type: 'text',
        label: 'Item List Header',
        helpText: 'Heading at the top of the itemList.',
        id: 'itemListheader',
        default: '<p>An Item List</p>',
    },
    hideListHeader: {
        type: 'boolean',
        label: 'Hide Series Header',
        id: 'hideListHeader',
        helpText: 'Hide the title that displays above the itemList.',
        default: false
    },
    headerStyles: {
        type: 'object',
        label: 'Header Styles',
        helpText: 'Select various styles for your header.',
        id: 'headerStyles',
        default: {
            backgroundColor: 'ffffffff',
            color: '000000ff',
            fontFamily: 'proxima-nova, sans-serif',
            fontSize: '20px',
            fontWeight: '600',
            textAlign: 'center'
        },
        properties: {
            backgroundColor: {
                type: 'color',
                label: 'Header Background Color',
                helpText: 'Color for the header background.',
                id: 'backgroundColor',
                noColor: true
            },
            color: {
                type: 'color',
                label: 'Header Text Color',
                helpText: 'Color for the header text.',
                id: 'color'
            },
            fontSize: {
                type: 'dropdown',
                label: 'Header Text Size',
                helpText: 'Color for the header text.',
                fields: fieldsObj,
                items: [
                    { text: '16px', value: '16px' },
                    { text: '18px', value: '18px' },
                    { text: '20px', value: '20px' },
                    { text: '24px', value: '24px' },
                    { text: '28px', value: '28px' },
                    { text: '36px', value: '36px' }
                ],
                id: 'headerFontSize',
                default: 'Header Font Size'
            },
            fontFamily: {
                type: 'dropdown',
                label: 'Title Font',
                helpText: 'The font type used for the header text.',
                fields: fieldsObj,
                items: fontList,
                id: 'fontFamily'
            },
            fontWeight: {
                type: 'dropdown',
                label: 'Title Weight',
                helpText: 'The weight of the header text.',
                fields: fieldsObj,
                items: fontWeights,
                id: 'fontWeight'
            },
            textAlign: {
                type: 'dropdown',
                label: 'Text Alignment',
                id: 'textAlign',
                default: 'center',
                helpText: 'Text alignment for the chart title: left, right, center.',
                fields: fieldsObj,
                items: [
                    { text: 'Center', value: 'center' },
                    { text: 'Align Left', value: 'left' },
                    { text: 'Align Right', value: 'right' },
                ]
            },
        }
    },
    datasetIds: {
        type: 'datasource',
        id: 'datasetIds',
        mode: 'Box',
        itemsSource: 'configStore.getDatasourceList()',
        fields: {
            text: 'text',
            value: 'value',
            parentValue: 'pId',
            hasChildren: 'hasChild'
        },
        allowFiltering: true,
        default: [],
        label: "Select a datasource",
        filterType: 'contains',
        // onchange: "selectDataset",
        helpText: 'Dataset that provides data for this item list',
        secondaryActions: [{
            text: 'Dataset Details',
            action: 'viewDataset',
            source: 'actionStore',
            params: {}
        }]
    },
    parentRecordType: {
        type: 'dropdown',
        label: 'Datasource Context Record',
        items: [
            { text: 'Context Volunteer', value: 'volId' },
            { text: 'Context Opportunity', value: 'oppId' },
            { text: 'Context Organization', value: 'orgId' },
            { text: 'Context Entry Point', value: 'entryPointId' },
            { text: 'Mapped Parent Records', value: 'parentRecords' },
        ],
        fields: {
            text: 'text',
            value: 'value'
        },
        id: 'parentRecordType',
        helpText: 'Use the record passed into the component from external source.'
    },
    hideWhenNoItems: {
        label: 'Hide this component when there are no visible items',
        type: 'boolean',
        id: 'hideWhenNoItems',
        helpText: 'This setting will hide the component when there are no items after all rendering. This is not affected by search returning no results',
        default: false
    },
    itemsListSource: {
        label: 'Source for Items',
        type: 'dropdown',
        items: [
            { text: 'User Configured', value: 'user_configured' },
            { text: 'itemsList Property', value: 'itemsList_prop' },
        ],
        fields: {
            text: 'text',
            value: 'value'
        },
        id: 'itemsListSource',
        helpText: 'Define the source for your items. If you choose "user Configured" you will be able to configure your own items.  If you choose "Items Property" the items will come from the itemList\'s itemsList property.'
    },
    itemList: {
        label: 'Items',
        id: 'itemList',
        type: 'ARRAY',
        default: [],
        helpText: 'Create a list of items that will be included and enabled for each record according to the include and enable property logical conditions.  Each item can have a title, description, one or more actions, and logical criteria for when a item is enabled and included',
        itemConfig: {
            itemType: 'object',
            primaryKey: 'humanId',
            permissions: {
                canMove: true,
                canDelete: true,
                allowAddItem: true
            },
            keys: {
                isComponentItem: {
                    type: 'boolean',
                    default: false,
                    id: 'isComponentItem',
                    label: 'Use Component as Item',
                    helpText: 'Bypass itemlist processing and display a component for each item.'
                },
                itemComponentId: {
                    type: 'string',
                    default: '',
                    id: 'itemComponentId',
                    label: 'Item Component ID',
                },
                humanId: {
                    type: 'string',
                    default: 'A Item',
                    id: 'humanId',
                    label: 'Item ID',
                    helpText: 'Give your item an ID so it is easy to recognize in the editor.'
                },
                // include: {
                //     type: 'BOOLEAN',
                //     default: true,
                //     id: 'include',
                //     label: 'Include by Default?',
                //     helpText: 'Should the item be included by default?'
                // },
                enable: {
                    type: 'BOOLEAN',
                    default: true,
                    id: 'enable',
                    label: 'Enable by Default?',
                    helpText: 'Should the item be enabled by default?'
                },
                complete: {
                    type: 'BOOLEAN',
                    default: false,
                    id: 'complete',
                    label: 'Complete by Default?',
                    helpText: 'Should the item be enabled by default?'
                },
                includeWhen: {
                    label: 'Display Item Logic',
                    id: 'includeWhen',
                    helpText: 'Set logic to determine when the item should be displayed in the list.',
                    ...setIncludeWhenLogicProps('List Item')
                },
                enableWhen: {
                    label: 'Enable Item Logic',
                    helpText: 'Set logic to determine when the item should be enabled in the list. Enabled items can show actions.',
                    id: 'enableWhen',
                    ...setIncludeWhenLogicProps('List Item')
                },
                activeWhen: {
                    label: 'Item is Active When',
                    helpText: 'Set logic to determine when the item should be active in the list. Active items show actions.',
                    id: 'activeWhen',
                    ...setIncludeWhenLogicProps('List Item')
                },
                completeWhen: {
                    label: 'Item is Complete When',
                    helpText: 'Set logic to determine when the item should marked as completed in the list.',
                    id: 'completeWhen',
                    ...setIncludeWhenLogicProps('List Item')
                },
                titles: {
                    type: 'ARRAY',
                    label: 'Item Titles',
                    id: 'titles',
                    itemConfig: {
                        primaryKey: 'titleLabel',
                        itemType: 'object',
                        permissions: {
                            canMove: true,
                            canDelete: true,
                            allowAddItem: true
                        },
                        keys: {
                            titleLabel: {
                                type: 'string',
                                id: 'titleLabel',
                                label: 'Title Label',
                                helpText: 'Label of the title for the settingsEditor UI.'
                            },
                            template: {
                                type: 'string',
                                id: 'template',
                                label: 'Title Text Template',
                                helpText: 'Text template for the title of the item.'
                            },
                            // include: {
                            //     label: 'Display Title Default',
                            //     id: 'include',
                            //     type: 'BOOLEAN',
                            //     helpText: 'Set whether the title is displayed by default. Turn this on if no title display logic is used.',
                            //     default: true
                            // },
                            hideWhenDisabled: {
                                label: 'Hide When Item Not Active',
                                id: 'hideWhenDisabled',
                                type: 'boolean',
                                default: false,
                                helpText: 'Hide the title when the step is not active. Note: Action Logic overrides this.'
                            },
                            hideWhenComplete: {
                                label: 'Hide When Item is Complete',
                                id: 'hideWhenComplete',
                                type: 'boolean',
                                default: false,
                                helpText: 'Hide the title when the step is complete. Note: Action Logic overrides this.'
                            },
                            includeWhen: {
                                label: 'Title Display Logic',
                                id: 'includeWhen',
                                helpText: 'Set logic to determine when the title should be displayed.',
                                ...setIncludeWhenLogicProps('Title')
                            }
                        },
                        newItem: {
                            titleLabel: 'Item Title',
                            template: '<strong>Title</strong>',
                            templateId: false,
                            // include: true,
                            hideWhenDisabled: false,
                            hideWhenComplete: false,
                            includeWhen: []
                        }
                    },
                    helpText: 'The title(s) for the item.'
                },
                descriptions: {
                    type: 'ARRAY',
                    id: 'descriptions',
                    label: 'Item Descriptions',
                    helpText: 'A item can have one ore multiple descriptions that are displayed based on logic.',
                    itemConfig: {
                        primaryKey: 'descriptionLabel',
                        itemType: 'object',
                        permissions: {
                            canMove: true,
                            canDelete: true,
                            allowAddItem: true
                        },
                        keys: {
                            descriptionLabel: {
                                type: 'string',
                                id: 'descriptionLabel',
                                label: 'Description Title',
                                helpText: 'Text you want to display in the description area.',
                                default: ''
                            },
                            template: {
                                type: 'text',
                                id: 'template',
                                label: 'HTML Template',
                                showExpandMinimizeBtns: true,
                                helpText: 'HTML template for the information you want to display in the description area. Click HERE for examples.',
                                default: '<p>Description</p>'
                            },
                            // include: {
                            //     label: 'Default Include',
                            //     id: 'include',
                            //     type: 'BOOLEAN',
                            //     default: true
                            // },
                            hideWhenDisabled: {
                                label: 'Hide When Item Not Active',
                                id: 'hideWhenDisabled',
                                type: 'boolean',
                                default: false,
                                helpText: 'Hide the description when the step is not active. Note: Action Logic overrides this.'
                            },
                            hideWhenComplete: {
                                label: 'Hide When Item is Complete',
                                id: 'hideWhenComplete',
                                type: 'boolean',
                                default: true,
                                helpText: 'Hide the description when the step is complete. Note: Action Logic overrides this.'
                            },
                            includeWhen: {
                                label: 'Include When',
                                id: 'includeWhen',
                                helpText: 'Set logic to determine when this description should be displayed.',
                                ...setIncludeWhenLogicProps('Description')
                            }
                        },
                        newItem: {
                            descriptionLabel: 'A description',
                            template: '<p>Description</p>',
                            templateId: false,
                            // include: true,
                            hideWhenDisabled: true,
                            hideWhenComplete: true,
                            includeWhen: []
                        }
                    }
                },
                actions: {
                    type: 'ARRAY',
                    id: 'actions',
                    label: 'Item Actions',
                    helpText: 'A item can have one ore multiple actions that are displayed based on logic.',
                    itemConfig: {
                        primaryKey: 'label',
                        itemType: 'object',
                        permissions: {
                            canMove: true,
                            canDelete: true,
                            allowAddItem: true
                        },
                        keys: {
                            label: {
                                type: 'string',
                                id: 'label',
                                label: 'Button Label',
                                helpText: 'The label for your action button.',
                                default: 'Action',
                            },
                            actionIcon: {
                                label: 'Action button icon',
                                type: 'iconpicker',
                                id: 'actionIcon',
                                helpText: 'Select an icon from the dropdown.'
                            },
                            actionTextColor: {
                                type: 'color',
                                label: 'Action button text color',
                                helpText: 'Color for the Text of the Action button.',
                                id: 'actionTextColor',
                                default: '#000000',
                            },
                            actionBackgroundColor: {
                                type: 'color',
                                label: 'Action button background color',
                                helpText: 'Color for the Background of the Action button.',
                                id: 'actionBackgroundColor',
                                default: '#ffffff00',
                            },
                            // include: {
                            //     label: 'Display Default',
                            //     id: 'include',
                            //     type: 'BOOLEAN',
                            //     helpText: 'Set whether you want the action button to display, or not, by default. Set this to true if you do not plan to use action display logic',
                            //     default: true
                            // },
                            hideWhenDisabled: {
                                label: 'Hide When Item Not Active',
                                id: 'hideWhenDisabled',
                                type: 'boolean',
                                default: true,
                                helpText: 'Hide the action button when the step is not active. Note: Action Logic overrides this.'
                            },
                            hideWhenComplete: {
                                label: 'Hide When Item is Complete',
                                id: 'hideWhenComplete',
                                type: 'boolean',
                                default: true,
                                helpText: 'Hide the action button when the step is complete. Note: Action Logic overrides this.'
                            },
                            includeWhen: {
                                label: 'Action Display Logic',
                                id: 'includeWhen',
                                helpText: 'Set logic to determine when the action button should be displayed.',
                                ...setIncludeWhenLogicProps('Action')
                            },
                            disableWhen: {
                                label: 'Item is Disabled When',
                                helpText: 'Set logic to determine when the action should be disabled.',
                                id: 'disableWhen',
                                ...setIncludeWhenLogicProps('List Item')
                            },
                            action: {
                                label: 'Action to Execute',
                                id: 'action',
                                type: 'actionfield',
                                itemsSource: 'actionStore.getActionOptions()',
                                fields: { text: 'text', value: 'value' },
                                helpText: 'Choose from a list of standard actions that you want to execute when a user clicks the action button.',
                                default: {
                                    action: '',
                                    params: {}
                                }
                            },
                            message: {
                                label: 'Action Message',
                                id: 'message',
                                type: 'string',
                                helpText: 'Display a message below the action button.',
                                default: ''
                            }
                        },
                        newItem: {
                            label: 'Click',
                            message: 'A message under the action',
                            actionAutomationId: false,
                            actionBCPackageId: false,
                            action: {
                                action: '',
                                params: {}
                            },
                            // include: true,
                            hideWhenDisabled: true,
                            hideWhenComplete: true,
                            includeWhen: [],
                            disableWhen: [],
                        }
                    }
                },
                itemActionDisplay: {
                    label: 'Display Action Buttons in a Row',
                    id: 'itemActionDisplay',
                    type: 'dropdown',
                    helpText: 'When turned on, the buttons will display in a row rather than a column (which is the default).',
                    fields: fieldsObj,
                    items: [
                        { text: 'default', value: '' },
                        { text: 'inline', value: 'action-btn-inline' }
                    ]
                },
                actionBorderRadius: {
                    type: 'string',
                    label: 'Action Button Border Radius',
                    id: 'actionBorderRadius',
                    default: '0',
                    helpText: 'Type in a number between 1-20 ending with a px (ie: 5px).'
                },
                enableEvenOddColors: {
                    label: 'Use Even/Odd Background Colors',
                    id: 'enableEvenOddColors',
                    type: 'BOOLEAN',
                    helpText: 'Set whether you want to use Even/Odd Background Colors or not.',
                    default: true
                },
                evenItemBackgroundColor: {
                    type: 'color',
                    label: 'Even Item Background Color',
                    helpText: 'Color for the Background.',
                    id: 'evenItemBackgroundColor',
                    default: '#FFFFFFFF',
                    logic: [
                        {
                            record: 'self',
                            criteriaRecord: 'component',
                            prop: 'display',
                            valueType: 'criteria',
                            criteria: "this.controls.itemList.value[__index__].enableEvenOddColors"
                        }
                    ]
                },
                oddItemBackgroundColor: {
                    type: 'color',
                    label: 'Odd Item Background Color',
                    helpText: 'Color for the Background.',
                    id: 'oddItemBackgroundColor',
                    default: '#EEEEEEFF',
                    logic: [
                        {
                            record: 'self',
                            criteriaRecord: 'component',
                            prop: 'display',
                            valueType: 'criteria',
                            criteria: "this.controls.itemList.value[__index__].enableEvenOddColors"
                        }
                    ]
                },
                itemBackgroundColor: {
                    type: 'color',
                    label: 'itemList Background Color',
                    helpText: 'Color for the Background.',
                    id: 'itemBackgroundColor',
                    default: '#ecececff',
                },
                itemCompleteColor: {
                    type: 'color',
                    label: 'Color signifying an item is Complete.',
                    helpText: 'Background color for Complete.',
                    id: 'itemCompleteColor',
                    default: '#efffec',
                },
                itemActiveColor: {
                    type: 'color',
                    label: 'Color signifying an item is Active.',
                    helpText: 'Background color for Active.',
                    id: 'itemActiveColor',
                    default: '#d8f9ff',
                },
                itemDisabledColor: {
                    type: 'color',
                    label: 'Color signifying an item is Disabled.',
                    helpText: 'Background color for Disabled.',
                    id: 'itemDisabledColor',
                    default: '#555555',
                },
            },
            newItem: {
                humanId: 'item_1',
                isComponentItem: false,
                itemComponentId: '',
                titles: [
                    {
                        titleLabel: 'Item Title',
                        template: '<strong>Title</strong>',
                        // include: true,
                        includeWhen: []
                    }
                ],
                descriptions: [
                    {
                        descriptionLabel: 'A description',
                        template: '<p>Description</p>',
                        // include: true,
                        includeWhen: []
                    }
                ],
                actions: [
                    {
                        label: 'Click',
                        message: 'A message under the action',
                        actionAutomationId: false,
                        actionBCPackageId: false,
                        action: {
                            action: '',
                            params: {}
                        },
                        // include: true,
                        hideWhenDisabled: true,
                        hideWhenComplete: true,
                        includeWhen: []
                    }
                ],
                // include: true,
                enable: true,
                includeWhen: [],
                enableWhen: [],
                evenItemBackgroundColor: '#FFFFFFFF',
                oddItemBackgroundColor: '#EEEEEEFF',
                itemBackgroundColor: '#ecececff',
                itemCompleteColor: '#efffec',
                // itemActiveColor: '#d8f9ff',
                // itemDisabledColor: '#555555'
            }
        },
        logic: [
            {
                record: 'self',
                criteriaRecord: 'component',
                prop: 'display',
                valueType: 'criteria',
                criteria: "!this.controls.itemsListSource.value || this.controls.itemsListSource.value === 'user_configured'"
            }
        ]
    },
    itemListHeight: {
        id: 'itemListHeight',
        type: 'dropdown',
        fields: fieldsObj,
        label: 'Max Height for the item-list-container. Anything beyond will have a scrollbar.',
        helpText: 'Choose a height between 300-1000px, 70% of the screen height, or all contents.',
        default: '70vh',
        items: [
            { text: '70% of the screen height', value: '70vh' },
            { text: 'Extra Small (300px)', value: '300px' },
            { text: 'Small (500px)', value: '500px' },
            { text: 'Medium (750px)', value: '750px' },
            { text: 'Large (1000px)', value: '1000px' },
            { text: 'Show All Contents', value: 'inherit' }
        ]

    },
    visibleItemCount: {
        type: 'number',
        label: 'Visible Count of Items',
        helpText: 'Number of items that will be visible at a given time.  Additional items will be available through pagination.',
        id: 'visibleItemCount'
    },
    showPaginationAtTop: {
        type: 'boolean',
        label: 'Display Top Pagination',
        helpText: 'Number of items that will be visible at a given time.  Additional items will be available through pagination.',
        id: 'showPaginationAtTop',
        default: false
    },
    showPaginationInHeader: {
        type: 'boolean',
        label: 'Display Header Pagination',
        helpText: 'Number of items that will be visible at a given time.  Additional items will be available through pagination.',
        id: 'showPaginationInHeader',
        default: false
    },
    avatarContactCardId: {
        type: 'dropdown',
        id: 'avatarContactCardId',
        itemsSource: 'componentStore.getList(true)',
        itemsFilter: 'this.item.cType === "contactCard"',
        fields: { text: 'cId', value: 'cId' },
        label: 'Avatar Contact Card ID',
        helpText: 'Choose the Contact Card to use for the Avatar Icon.',
        logic: [
            {
                record: 'self',
                criteriaRecord: 'component',
                prop: 'display',
                valueType: 'criteria',
                criteria: "this.controls.itemIconType.value === 'avatar'"
            }
        ]
    },
    itemIconType: {
        id: 'itemIconType',
        type: 'dropdown',
        items: [
            { text: 'None', value: 'none' },
            { text: 'Number', value: 'number' },
            { text: 'Avatar Image', value: 'avatar' },
            { text: 'First Name Initial', value: 'first_initial' },
            { text: 'Last Name Initial', value: 'last_initial' },
        ],
        fields: { text: 'text', value: 'value' },
        label: 'Main Icon for Item',
        helpText: 'Specify the icon for each item.',
        default: 'number'
    },
    itemIconSize: {
        id: 'itemIconSize',
        type: 'dropdown',
        items: [
            { text: 'Small', value: 'small' },
            { text: 'Medium', value: 'medium' },
            { text: 'Large', value: 'large' },
        ],
        fields: { text: 'text', value: 'value' },
        helpText: 'Specify the size of the item icon.',
        label: 'Item Icon Size',
        default: 'small',
        logic: [
            {
                record: 'self',
                prop: 'display',
                valueType: 'criteria',
                criteria: "this.controls.itemIconType.value === 'number' || this.controls.itemIconType.value === 'avatar' || this.controls.itemIconType.value === 'first_initial' || this.controls.itemIconType.value === 'last_initial'"
            }
        ],
    },
    iconStyles: {
        type: 'OBJECT',
        label: 'Icon Styles',
        id: 'iconStyles',
        helpText: 'Settings specific to the Icon in your itemList',
        default: {
            backgroundColor: '#c6c6c6',
            color: '#555555'
        },
        logic: [
            {
                record: 'self',
                prop: 'display',
                valueType: 'criteria',
                criteria: "this.controls.itemIconType.value === 'number' || this.controls.itemIconType.value === 'first_initial' || this.controls.itemIconType.value === 'last_initial'"
            }
        ],
        properties: {
            backgroundColor: {
                type: 'color',
                label: 'Icon Background Color',
                helpText: 'Color for the itemList icon.',
                id: 'backgroundColor',
                noColor: true,
                logic: [
                    {
                        record: 'self',
                        criteriaRecord: 'component',
                        prop: 'display',
                        valueType: 'criteria',
                        criteria: "this.controls.itemIconType.value === 'number' || this.controls.itemIconType.value === 'first_initial' || this.controls.itemIconType.value === 'last_initial'"
                    }
                ]
            },
            color: {
                type: 'color',
                label: 'Icon Font Color',
                helpText: 'Choose a color for the itemList Icon Font.',
                id: 'color',
                logic: [
                    {
                        record: 'self',
                        criteriaRecord: 'component',
                        prop: 'display',
                        valueType: 'criteria',
                        criteria: "this.controls.itemIconType.value === 'number' || this.controls.itemIconType.value === 'first_initial' || this.controls.itemIconType.value === 'last_initial'"
                    }
                ]
            }
        }
    },
    sortBy: {
        id: 'sortBy',
        type: 'dropdown',
        label: 'Primary Item Sort Method',
        items: [
            { label: 'record', value: 'record' },
            { label: 'item type', value: 'item type' }
        ],
        fields: {
            text: 'label',
            value: 'value'
        },
        helpText: 'Set how items should be grouped/sorted.',
    },
    sortField: {
        id: 'sortField',
        type: 'dropdowntree',
        label: 'Sort Field',
        itemsSource: 'configStore.allFieldOptions()',
        allowFiltering: true,
        filterType: 'Contains',
        fields: {
            text: 'text',
            value: 'value',
            hasChildren: 'hasChild',
            parentValue: 'pId',
        },
        helpText: 'Set which field you want to sort by.',
        logic: [
            {
                record: 'self',
                criteriaRecord: 'component',
                prop: 'display',
                valueType: 'criteria',
                criteria: "this.controls.sortBy.value === 'record'"
            }
        ]
    },
    sortDirection: {
        type: 'dropdown',
        label: 'Direction',
        default: 'asc',
        items: [
            'asc',
            'desc'
        ],
        id: 'direction',
        logic: [
            {
                record: 'self',
                criteriaRecord: 'component',
                prop: 'display',
                valueType: 'criteria',
                criteria: "this.controls.sortBy.value === 'record'"
            }
        ]
    },
    noRecordsMessage: {
        type: 'text',
        id: 'noRecordsMessage',
        label: 'No Records to Display Template',
        helpText: 'Write and style a custom message that will show if there are no records to display.',
        default: '<p>There are no records to display.</p>'
    },
    printExpandCollapseGroupedItems: {
        type: 'dropdown',
        id: 'printExpandCollapseGroupedItems',
        label: 'Group Expanding Options',
        items: [
            { text: 'Leave As Is', value: 'as_is' },
            { text: 'Expand All', value: 'expand' },
            { text: 'Collapse All', value: 'collapse' },
        ],
        fields: { text: 'text', value: 'value' },
        logic: [
            {
                record: 'self',
                prop: 'display',
                valueType: 'criteria',
                criteria: 'this.valuesMap.enablePrinting'
            }
        ],
        panel: 'commonPrintSettingsPanel'
    }
};

const itemListConfig = {
    cId: "itemList",
    cType: "itemList",
    cTitle: "Item List",
    cTemplate: `<div v-if="!configReload && config && (!config.hideWhenNoItems || mappedItems.length)" :style="componentStyles">
                    <div class="item-list-container" :class="{'is-dragging': config.allowDragAndDrop && dragIndex !== false }" :style="itemListStyles" ref="itemList">
                        <div v-if="!config.hideListHeader" class="item-list-header" :style="headerStyles" >
                            <component :is="headerTemplate" v-if="headerTemplate && initialLoadComplete" class="header-text"></component>
                            <div class="list-pagination" v-if="pages.length && config.showPaginationInHeader">
                                <div>
                                    <div class="total-page-count">Items {{startingItem}} - {{endingItem}}<span v-if="availableRecords.length > 0"> / {{availableRecords.length}}</span></div>
                                    <control name="CURRENT_PAGE" :value="currentPage" :items="pages" @update:value="currentPage = $event" class="current-page"></control>
                                </div>
                            </div>
                        </div>
                        <div v-if="config.enableFilter">
                            <condensed-filter
                                :prop-filter-options="filterOptions"
                                :prop-item-label="'records'"
                                :cId="'condensedFilter__' + cId"
                                :prop-filter-items="mappedItems"
                                prop-sub-object-key="record"
                                :prop-additional-config="config.additionalConfig"
                                :prop-result="filteredItems"
                                @update:prop-result="filteredItems = $event; filteredItemsUpdated()"
                                :prop-filtering="filtering"
                                @update:prop-filtering="filtering = $event"></condensed-filter>
                        </div>
                        <div class="list-pagination" v-if="pages.length && config.showPaginationAtTop">
                            <div>
                                <div class="total-page-count">Items {{startingItem}} - {{endingItem}}<span v-if="availableRecords.length > 0"> / {{availableRecords.length}}</span></div>
                                <control name="CURRENT_PAGE" :value="currentPage" :items="pages" @update:value="currentPage = $event" class="current-page"></control>
                            </div>
                        </div>
                        <div v-if="config.noRecordsMessage && initialLoadComplete && !itemsLength" v-html="config.noRecordsMessage" class="no-records_msg"></div>
                        <div v-if="!groupedBy">
                            <div v-show="!initialLoadComplete || (itemListDisableOnSubscriptions && componentPendingSubscriptions.length)">
                                <div v-for="key in 3">
                                   <div class="skeleton list-item">
                                        <ejs-skeleton shape="Text" width="250px" height="2.5rem" class="header-skeleton"></ejs-skeleton>
                                        <ejs-skeleton shape="Rectangle" width="150px" height="2rem" class="button-skeleton"></ejs-skeleton>
                                   </div>
                               </div>
                            </div>
                           <div v-if="!itemListDisableOnSubscriptions || !componentPendingSubscriptions.length"
                                v-for="(data, index) in visibleItems"
                                :id="data.elemId"
                                class="list-item"
                                :class="[data.cssClasses, {'dragged-item': dragIndex === data.itemIndex, 'item-draggable': isDraggable, 'list-component-item': data.isComponentItem}]"
                                :draggable="config.allowDragAndDrop && dragIndex === data.itemIndex"
                                @dragstart="onDragstart($event, data)"
                                @dragend="onDragend($event, data)"
                                @click="targetItem(index, data)"
                                :key="index"
                           >
                                <div v-if="config.allowDragAndDrop"
                                     @mousedown="dragIndex = data.itemIndex;"
                                     @mouseup="dragIndex = false;"
                                     :id="'drag_handle_' + data.elemId"
                                     class="drag-drop-handle"
                                >
                                    <span class="e-drag-and-drop"><i class="fa-solid fa-grip-dots-vertical"></i></span>
                                </div>
                                <div :id="data.elemId + '_drop_zone'"
                                     class="drop-zone"
                                     @drop="onDrop($event, data)"
                                     @dragover="onDragover($event, data)"
                                     @dragleave="onDragleave"
                                >
                                    <div class="drop-indicator">{{}}</div>
                                </div>
                                <div v-if="data.isComponentItem" class="list-item-inner">
                                    <component is="activityStepPanel"
                                        c-type="activityStepPanel"
                                        :cId="data.itemComponentId + '__' + cId + '_' + uniqueId + '_' + data.ACTIVITY_ENTITY_ID + '_' + (data.AFFILIATION_ID || data.record?.AFFILIATION_ID)"
                                        :ref="data.itemComponentId + '__' + cId + '_' + uniqueId + '_' + data.ACTIVITY_ENTITY_ID + '_' + (data.AFFILIATION_ID || data.record?.AFFILIATION_ID)"
                                        :record="data.record"
                                        :workflow="data.workflow"
                                        :prev-step="index > 0 ? visibleItems[index - 1] : null"
                                        :next-step="index !== visibleItems.length - 1 ? visibleItems[index + 1] : null"
                                        :context-vol-id="data?.VOLUNTEER_ID || data.record?.VOLUNTEER_ID"
                                        :context-aff-id="data?.AFFILIATION_ID || data.record?.AFFILIATION_ID"
                                        :context-ae-id="data?.ACTIVITY_ENTITY_ID || data.record?.ACTIVITY_ENTITY_ID"
                                        :hovered="data.hovered"
                                        :targeted="targetedItemIndex === data.itemIndex"
                                        :index="index"
                                        :context-opp-id="oppId"
                                        :workflow-order-index="index"
                                        :prop-editor-visible="editorVisible"
                                        @ready="$emit('ready', $event)"
                                        @openDynamicSurveys="$emit('openDynamicSurveys', $event)"
                                    ></component>
                                </div>
                                <div v-else class="list-item-inner">
                                    <div class="item-icon" :class="[ 'icon-' +config.itemIconSize, 'icon-' + config.itemIconType ]" :style="iconStyles">
                                        <span v-if="config.itemIconType === 'number'">{{data.number}}</span>
                                        <span v-if="config.itemIconType === 'first_initial' || config.itemIconType === 'last_initial'">{{data.iconInitials}}</span>
                                        <contact-card
                                            v-if="config.itemIconType === 'avatar'"
                                            c-type="contactCard"
                                            :cId="config.avatarContactCardId ? config.avatarContactCardId + '__item__' + config.cId + '__' + data.itemIndex : 'contactCard__' + data.itemIndex"
                                            :prop-contact-id="data.record.uiRecordId"
                                            :prop-contact-psp="data.record.uiRecordPsp"
                                        ></contact-card>
                                    </div>
                                    <div class="item-content">
                                        <div v-for="(title, iindex) in data.activeTitles" :class="title.cssClasses" :key="iindex">
                                            <component :is="title.tmpTemplateId()" :record="data.record" :hovered="data.hovered" :targeted="targetedItemIndex === data.itemIndex" :index="data.itemIndex" class="item-title"></component>
                                        </div>
                                        <div v-for="(description, iindex) in data.activeDescriptions" :class="description.cssClasses" :key="iindex">
                                            <component :is="description.tmpTemplateId()" :record="data.record" :hovered="data.hovered" :targeted="targetedItemIndex === data.itemIndex" :index="data.itemIndex"></component>
                                        </div>

                                    </div>
                                    <div class="item-action-area action-ctrls" :class="data.actionButtonDisplay">
                                        <div v-for="(action, iindex) in data.activeActions"
                                             class="item-action-item e-icons"
                                             :class="data.actionIcon"
                                             :key="action.actionId"
                                        >
                                            <control :name="action.actionId"
                                                     :key="action.actionId"
                                                     :ref="action.ref || action.id"
                                                     :actionParams="[action, data]"
                                                     data-test-id="action.dataTestId"
                                                     :class="action.cssClasses"
                                                     :disabled="action.disabled"
                                                     :label="action.label"
                                                     :style="{color:action.actionTextColor, background:action.actionBackgroundColor, borderRadius:data.actionBorderRadius}"
                                                     :prop-config="{
                                                            removeOnDestroy: true,
                                                            hideLabel: action.hideLabel,
                                                            title: action.title,
                                                            type: 'button',
                                                            actionIcon: action.ACTION_ICON || action.actionIcon,
                                                            onclick: actionClick
                                                     }"
                                            ></control>
                                            <p v-if="action.message">{{action.message}}</p>
                                        </div>
                                    </div>
                                    <div class="item-action-area complete-indicator" v-if="data.complete">
                                        <span class="e-icons e-complete"></span>
                                        <span style="font-size:20px; font-weight: bold;">Complete</span>
                                    </div>
                                </div>
                                <div v-if="index === visibleItemsLength - 1"
                                     :id="data.elemId + '_drop_zone'"
                                     class="drop-zone last-drop-zone"
                                     @drop="onDrop($event, data, true)"
                                     @dragover="onDragover($event, data, true)"
                                     @dragleave="onDragleave"
                                >
                                    <div class="drop-indicator">{{}}</div>
                                </div>
                            </div>
                        </div>
                        <div class="grouped-items" v-if="groupedBy">
                            <div v-show="!initialLoadComplete">
                                <div v-for="key in 3"
                                 class="group-container skeleton"
                                >
                                    <div class="list-item group-header">
                                        <ejs-skeleton height="25px" width="25px" style="margin-right: 0.5rem"></ejs-skeleton>
                                        <ejs-skeleton height="1.5rem" width="300px" shape="Text"></ejs-skeleton>
                                    </div>
                                </div>
                            </div>

                            <!-------------------------------->
                            <!--  display for grouped items -->
                            <!-------------------------------->
                            <div v-if="visibleItems && initialLoadComplete">
                                <div v-for="(value, key) in visibleItems"
                                 :key="key"
                                 class="group-container"
                                 :record-count="value.itemRecordsCount"
                                 :group="value.group"
                                >
                                    <div class="list-item group-header" @click="expandGroup(value) && value.expandItem">
                                        <div class="group_div">
                                            <div>
                                                <i v-if="!expandedGroupItems[value.group] && value.expandItem" class="fa-solid fa-chevron-right"></i>
                                                <i v-if="expandedGroupItems[value.group] && value.expandItem" class="fa-solid fa-chevron-down"></i>
                                            </div>
                                            <component v-if="config.customGroupHeaders"
                                                       :is="value.headerId"
                                                       :record="value"
                                                       :item-records="value.itemRecords"
                                            ></component>
                                            <span v-else-if="!config.customGroupHeaders" style="line-height: 2rem">{{ value.group }}</span>
                                        </div>
                                    </div>
                                    <transition name="fade">
                                        <div v-if="expandedGroupItems[value.group] && initialLoadComplete" class="group-expanded-container">
                                            <div v-for="(data, index) in value.items"
                                                :id="data.elemId"
                                                class="grouped-item list-item"
                                                :class="[{'dragged-item': dragIndex === data.itemIndex}, data.cssClasses]"
                                                :draggable="config.allowDragAndDrop && dragIndex === data.itemIndex"
                                                @dragstart="onDragstart($event, data)"
                                                @dragend="onDragend($event, data)"
                                                @click="targetItem($event, data)"
                                                :key="index"
                                                :style="{ animationDelay: (index * 100) + 'ms' }"
                                            >
                                                <div v-if="config.allowDragAndDrop"
                                                     @mousedown="dragIndex = data.itemIndex;"
                                                     @mouseup="dragIndex = false;"
                                                     :id="'drag_handle_' + data.elemId"
                                                     class="drag-drop-handle"
                                                >
                                                    <span class="e-drag-and-drop"><i class="fa-solid fa-grip-dots-vertical"></i></span>
                                                </div>
                                                <div :id="data.elemId + '_drop_zone'"
                                                     class="drop-zone"
                                                     @drop="onDrop($event, data)"
                                                     @dragover="onDragover($event, data)"
                                                     @dragleave="onDragleave"
                                                >
                                                    <div class="drop-indicator">{{}}</div>
                                                </div>
                                                <div class="item-icon" :class="[ 'icon-' +config.itemIconSize, 'icon-' + config.itemIconType ]" :style="iconStyles">
                                                    <span v-if="config.itemIconType === 'number'">{{index + 1}}</span>
                                                    <span v-if="config.itemIconType === 'first_initial' || config.itemIconType === 'last_initial'">{{data.iconInitials}}</span>
                                                    <contact-card
                                                        v-if="config.itemIconType === 'avatar'"
                                                        c-type="contactCard"
                                                        :cId="config.avatarContactCardId ? config.avatarContactCardId + '__item__' + config.cId + '__' + data.itemIndex : 'contactCard__' + data.itemIndex"
                                                        :prop-contact-id="data.record.uiRecordId"
                                                        :prop-contact-psp="data.record.uiRecordPsp"
                                                    ></contact-card>
                                                </div>
                                                <div class="item-content">
                                                    <div v-for="(title, iindex) in data.activeTitles" :class="title.cssClasses" :key="iindex">
                                                        <component :is="title.tmpTemplateId()" :record="data.record" :hovered="data.hovered" :targeted="targetedItemIndex === data.itemIndex" :index="data.itemIndex" class="item-title"></component>
                                                    </div>
                                                    <div v-for="(description, iindex) in data.activeDescriptions" :class="description.cssClasses" :key="iindex">
                                                        <component :is="description.tmpTemplateId()" :record="data.record" :hovered="data.hovered" :targeted="targetedItemIndex === data.itemIndex" :index="data.itemIndex"></component>
                                                    </div>
                                                </div>
                                                <div class="item-action-area action-ctrls" :class="data.actionButtonDisplay">
                                                    <div v-for="(action, iindex) in data.activeActions"
                                                         class="item-action-item e-icons"
                                                         :class="data.actionIcon"
                                                         :key="action.actionId"
                                                    >
                                                        <control :name="action.actionId"
                                                                 :key="action.actionId"
                                                                 :ref="action.ref || action.id"
                                                                 :actionParams="[action, data]"
                                                                 data-test-id="action.dataTestId"
                                                                 :class="action.cssClasses"
                                                                 :disabled="action.disabled"
                                                                 :label="action.label"
                                                                 :style="{color:action.actionTextColor, background:action.actionBackgroundColor, borderRadius:data.actionBorderRadius}"
                                                                 :prop-config="{
                                                                        removeOnDestroy: true,
                                                                        hideLabel: action.hideLabel,
                                                                        type: 'button',
                                                                        actionIcon: action.ACTION_ICON || action.actionIcon,
                                                                        onclick: actionClick
                                                                 }"
                                                        ></control>
                                                        <p v-if="action.message">{{action.message}}</p>
                                                    </div>
                                                </div>
                                                <div class="item-action-area complete-indicator" v-if="data.complete">
                                                    <span class="e-icons e-complete"></span>
                                                    <span style="font-size:20px; font-weight: bold;">Complete</span>
                                                </div>
                                                <div v-if="index === visibleItems.length - 1"
                                                     :id="data.elemId + '_drop_zone'"
                                                     class="drop-zone last-drop-zone"
                                                     @drop="onDrop($event, data, true)"
                                                     @dragover="onDragover($event, data, true)"
                                                     @dragleave="onDragleave"
                                                >
                                                    <div class="drop-indicator">{{}}</div>
                                                </div>
                                            </div>
                                        </div>
                                    </transition>
                                </div>
                            </div>
                        </div>
                        <div class="item-count-display">
                            <div class="list-pagination" v-if="pages.length > 1">
                                <div>
                                    <div class="total-page-count">Items {{startingItem}} - {{endingItem}}<span v-if="availableRecords.length > 0"> / {{availableRecords.length}}</span></div>
                                    <control name="CURRENT_PAGE" :value="currentPage" :items="pages" @update:value="currentPage = $event" class="current-page"></control>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>`,
    controls: [
        {
            name: 'CURRENT_PAGE',
            label: 'Current Page',
            type: 'dropdown',
            width: '110px',
            default: 1,
            showClearButton: false,
            hideLabel: true,
            fields: {
                text: 'text',
                value: 'value'
            }
        }
    ]
};

let itemListCmp = {
    name: 'itemList',
    editorSettings: editorSettings,
    dataTestIds: [
        {
            search: '.item-list .list-item',
            cData: 'itemList._index_.itemTitle',
            suffix: 'item'
        },
        {
            search: '.item-list .list-item .item-icon',
            cData: 'itemList._index_.itemTitle',
            suffix: 'title'
        },
        {
            search: '.item-list .list-item .item-title',
            cData: 'itemList._index_.itemTitle',
            suffix: 'title'
        },
        {
            search: '.item-list .list-item .item-description',
            cData: 'itemList._index_.itemTitle',
            suffix: 'description'
        },
        {
            search: '.item-list .list-item .item-action-area',
            cData: 'itemList._index_.itemTitle',
            suffix: 'action'
        }
    ],
    props: {
        relativeRecords: {
            type: Array,
            default: [],
            excludeFromStore: true
        },
        parentRecords: {
            type: Object,
            default: () => ({})
        },
        itemsList: {
            type: Array,
            default: [],
            excludeFromStore: true
        },
        selectedItem: {},
        editorVisible: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            headerTemplate: false,
            targetedItemIndex: false,
            dragIndex: false,
            expandedGroupItems: {},
            prePrintExpandedGroupItems: {},
            initialLoadComplete: false,
            itemsLength: 0,
            // datasetRecords: false,
            // itemTemplate: false,
            // itemTemplateId: false,
            // templatesGenerated: false,
            filtering: false,
            filteredItems: [],
            // volRecordIds: [],
            // oppRecordIds: [],
            // orgRecordIds: [],
            // allRecordIds: {},
            currentPage: 1,
            itemListUseTemplateFnc: library.optimization.itemListUseTemplateFnc,
            itemListDisableOnSubscriptions: library.optimization.itemListDisableOnSubscriptions,
            visibleItemsLength: 0,
            uniqueId: library.getUniqueID()
        };
    },
    watch: {
        currentPage(oldValue, newValue) {
            if (!newValue) this.currentPage = 1;
        }
    },
    computed: {
        filterOptions() {
            let output = {};
            if (this.config.filterOptionsList && this.config.filterOptionsList.length) {
                this.config.filterOptionsList.forEach((option) => {
                    output[option.filterKey] = {
                        label: option.label,
                        type: option.type
                    };
                });
            }
            return output;
        },
        itemListStyles() {
            return { 'max-height': this.config.itemListHeight || 'none' };
        },
        iconStyles() {
            return this.config.iconStyles;
        },
        itemList() {
            // Put workflow config stuff here
            let items = [];
            if (this.config) {
                if (this.config.itemsListSource === 'itemsList_prop') items = this.itemsList; // from props
                else if (this.config.itemList) items = this.config.itemList;
            }
            return items;
        },
        expandGroupValue() {
            return this?.config?.expandGroupDefault;
        },
        includedItems() {
            if (library.optimization.itemListUseTmpComputedWhileSubscriptions) {
                if (this.componentPendingSubscriptions.length && this.tmpIncludedItems.length) return this.tmpIncludedItems;
            }
            let output = [];
            let currIndex = 0;
            let mergeSubRecords = (mergeSettings, item, record) => {
                // let subRecords = record.activityAffiliations;
                let subRecords = record[mergeSettings.subSourceKey];
                if (typeof subRecords !== 'object') return record;
                if (!Array.isArray(subRecords)) subRecords = Object.values(subRecords);
                let subRecord = subRecords.find((subRecord) => {
                    let _this = {
                        subRecord,
                        record,
                        item
                    };
                    return library.eval.call(_this, mergeSettings.subRecordFilter);
                });
                if (typeof subRecord === 'object') {
                    if (mergeSettings.mergeWithParent) return library.mergeObj({}, subRecord, record);
                    record[mergeSettings.parentOutputKey] = subRecord;
                }
                return record;
            };
            let setCurrItem = (item, record) => {
                record = library.cloneObj(record);
                let newItem = library.cloneObj(item);
                if (item.enableSubRecordMerging && Array.isArray(item.subRecordMergeRules)) {
                    item.subRecordMergeRules.forEach((mergeSettings) => {
                        record = mergeSubRecords(mergeSettings, item, record);
                    });
                }
                ['vol', 'opp', 'org', 'lbe', 'sch', 'ae', 'aff'].forEach((tmpPsp) => {
                    if (item[tmpPsp + 'Id']) record[tmpPsp + 'Id'] = item[tmpPsp + 'Id'];
                });
                if (item['AE_TITLE']) record['AE_TITLE'] = item['AE_TITLE'];
                if (item['WORKFLOW_TITLE']) record['WORKFLOW_TITLE'] = item['WORKFLOW_TITLE'];
                newItem.record = record;
                let included = true;
                if (newItem.includeWhen && newItem.includeWhen.length) {
                    included = library.logicParser.call(this, newItem.includeWhen, newItem, newItem.record);
                }
                let visible = true;
                if (newItem.visibleWhen && newItem.visibleWhen.length) {
                    visible = library.logicParser.call(this, newItem.visibleWhen, newItem, newItem.record);
                }
                if (typeof visible !== 'boolean') visible = true;
                if (typeof included === 'boolean' && typeof visible === 'boolean') newItem.include = included && visible;
                // Add the cssClasses property to all included items
                if (this.editorVisible) {
                    if (!newItem.cssClasses) newItem.cssClasses = '';
                    newItem.cssClasses += ' not-included';
                    newItem.include = true;
                }
                if (newItem.include) {
                    newItem.itemIndex = currIndex;
                    newItem.elemId = this.cId.replace(" ", /_/g).toLowerCase()
                        + "_list_item_" + currIndex;
                    currIndex++;
                    output.push(newItem);
                }
            };
            let recordList = library.cloneObj(this.recordList);
            let itemList = library.cloneObj(this.itemList);
            if (!itemList) itemList = [];
            if (this.config.sortBy === 'record') {
                if (!Array.isArray(this.config.sortField)) this.config.sortField = [];
                const sortField = this.config.sortField.map((thisSortField) => thisSortField.split(':::')[1]);
                if (recordList && sortField) library.sortObject(recordList, sortField, this.config.sortDirection);
                recordList.forEach((record) => {
                    itemList.forEach((item) => {
                        setCurrItem(item, record);
                    });
                });
            } else {
                itemList.forEach((item) => {
                    recordList.forEach((record) => {
                        setCurrItem(item, record);
                    });
                });
            }
            if (this.includedItemsLength !== output.length) {
                this.includedItemsLength = output.length;
                this.$emit('update:includedItemsLength', output.length);
            }
            if (!library.isEqualObj(this.tmpIncludedItems, output)) {
                this.tmpIncludedItems = output;
                this.$emit('update:includedItems', output);
            }
            return output;
        },
        mappedItems() {
            if (library.optimization.itemListUseTmpComputedWhileSubscriptions) {
                if (this.componentPendingSubscriptions.length && this.tmpMappedItems.length) return this.tmpMappedItems;
            }
            let includedItems = library.cloneObj(this.includedItems);
            let setCurrItem = (item, index) => {
                let record = item.record;
                let prevItem = includedItems[index - 1];
                let nextItem = includedItems[index + 1];
                let logicResult = true;

                if (item.hasOwnProperty('WORKFLOW_ID')) {
                    item.previousStepsComplete = (prevItem?.complete && prevItem?.previousStepsComplete) || !prevItem;
                }

                if (item.enableWhen && item.enableWhen.length) {
                    logicResult = library.logicParser.call(this, item.enableWhen, item, item.record, prevItem, nextItem);
                    if (typeof logicResult === 'boolean') item.enable = logicResult;
                }

                if (item.activeWhen && item.activeWhen.length) {
                    logicResult = library.logicParser.call(this, item.activeWhen, item, item.record, prevItem, nextItem);
                    if (typeof logicResult === 'boolean') item.active = logicResult;
                }

                if (item.completeWhen && item.completeWhen.length) {
                    logicResult = library.logicParser.call(this, item.completeWhen, item, item.record, prevItem, nextItem);
                    if (typeof logicResult === 'boolean') item.complete = logicResult;
                }
                // Set enable, active, and complete styles
                if (item.enable) item.cssClasses += ' item-enabled';
                if (item.active) item.cssClasses += ' item-active';
                if (item.complete) item.cssClasses += ' item-complete';
                // if editorVisible is toggled on: enable, activate, and complete all items to display all buttons and messages.
                if (this.editorVisible) {
                    if (!item.enable) {
                        item.cssClasses += ' not-enabled';
                        item.enable = true;
                    }
                    if (!item.active) {
                        item.cssClasses += ' not-active';
                        item.active = true;
                    }
                    if (!item.complete) {
                        item.cssClasses += ' not-complete';
                        item.complete = true;
                    }
                }
                // Parse Actions
                let currActions = library.cloneObj(item.actions);
                if (!Array.isArray(currActions)) currActions = [];
                currActions.forEach((action, index) => {
                    action.actionId = `action-item_${this.cId.replace(" ", /_/g).toLowerCase()}_${this.uniqueId}_${item.itemIndex}_${index}`;
                    if (!action.cssClasses) action.cssClasses = '';
                    let included = true;
                    let disabled = false;
                    if (item.complete && action.hideWhenComplete) included = false;
                    if (!item.complete && !item.enable && action.hideWhenDisabled) included = false;
                    if (included && action.includeWhen && action.includeWhen.length) {
                        included = library.logicParser.call(this, action.includeWhen, item.actions[index], record, prevItem, nextItem);
                    }
                    if (included && action.disableWhen && action.disableWhen.length) {
                        disabled = library.logicParser.call(this, action.disableWhen, item.actions[index], record, prevItem, nextItem);
                    }
                    if (!included && this.editorVisible) {
                        action.cssClasses += ' not-included';
                        included = true;
                    }
                    if (typeof included === 'boolean') action.include = included;
                    if (typeof disabled === 'boolean') action.disabled = disabled;
                });
                let activeActions = currActions.filter((action) => action.include === true);
                // Parse Descriptions
                let currDescriptions = library.cloneObj(item.descriptions);
                if (!Array.isArray(currDescriptions)) currDescriptions = [];
                currDescriptions.forEach((description) => {
                    if (description) {
                        let included = true;
                        if (item.complete && description.hideWhenComplete) included = false;
                        if (!item.complete && !item.enable && description.hideWhenDisabled) included = false;
                        if (included && description.includeWhen && description.includeWhen.length) {
                            included = library.logicParser.call(this, description.includeWhen, description, record, prevItem, nextItem);
                        }
                        if (!included && this.editorVisible) {
                            if (!description.cssClasses) description.cssClasses = '';
                            description.cssClasses += ' not-included';
                            included = true;
                        }
                        if (typeof included === 'boolean') description.include = included;
                    }
                });
                let activeDescriptions = currDescriptions.filter((description) => description.include === true);
                // Parse Titles
                let currTitles = library.cloneObj(item.titles);
                if (!Array.isArray(currTitles)) currTitles = [];
                currTitles.forEach((title) => {
                    let included = true;
                    if (item.complete && title.hideWhenComplete) included = false;
                    if (included && !item.complete && !item.enable && title.hideWhenDisabled) included = false;
                    if (included && title.includeWhen && title.includeWhen.length) {
                        included = library.logicParser.call(this, title.includeWhen, title, record, prevItem, nextItem);
                    }
                    if (!included && this.editorVisible) {
                        if (!title.cssClasses) title.cssClasses = '';
                        title.cssClasses += ' not-included';
                        included = true;
                    }
                    if (typeof included === 'boolean') title.include = included;
                });
                let activeTitles = currTitles.filter((title) => title.include === true);
                let bgColor;
                if (!item.enableEvenOddColors) {
                    bgColor = item.itemBackgroundColor;
                } else {
                    bgColor = index % 2 === 0 ? item.evenItemBackgroundColor : item.oddItemBackgroundColor;
                }
                if (item.complete && item.itemCompleteColor) bgColor = item.itemCompleteColor;
                else if (!item.enable && item.itemDisabledColor) bgColor = item.itemDisabledColor;
                else if (item.active && item.itemActiveColor) bgColor = item.itemActiveColor;
                let newItem = library.cloneObj(item);
                newItem = {
                    ...newItem,
                    record,
                    enable: item.enable,
                    complete: item.complete,
                    active: false,
                    hovered: false,
                    selected: false,
                    activeTitles,
                    activeDescriptions,
                    activeActions,
                    itemIndex: item.itemIndex,
                    backgroundColor: bgColor,
                    actionButtonDisplay: item.itemActionDisplay,
                    actionBorderRadius: item.actionBorderRadius
                };
                if (this.config.itemIconType === 'first_initial') {
                    newItem.iconInitials = record.VOL_FIRST_NAME.substring(0, 1);
                }
                else if (this.config.itemIconType === 'last_initial') {
                    newItem.iconInitials = record.VOL_LAST_NAME.substring(0, 1);
                }
                // Run actions after everything else has been processed
                // TODO make this configurable
                if (item.onChangeActions && !this.componentPendingSubscriptions.length) {
                    let cActions = item.onChangeActions;
                    cActions.forEach((iAction) => {
                        let actionResult = true;
                        if (!iAction.runOnce || (iAction.runOnce && (!runOnceActions[this.cId] || !runOnceActions[this.cId][index]))) {
                            if (iAction.logic) {
                                actionResult = library.logicParser.call(this, iAction.logic, item, item.record, prevItem, nextItem);
                            }
                            if (typeof iAction.executeIfLogicResult !== 'undefined' && actionResult !== iAction.executeIfLogicResult) return;
                            if (!runOnceActions[this.cId]) runOnceActions[this.cId] = {};
                            // TODO: Maybe change this to make it more unique
                            if (!runOnceActions[this.cId][index]) {
                                runOnceActions[this.cId][index] = actionResult;
                                if (Array.isArray(iAction.actions)) {
                                    actionStore.runChainedActions(iAction.actions, { ...item.record, prevItem, nextItem }, this);
                                }
                            }
                        }
                    });
                }
                return newItem;
            };
            let mappedItems = includedItems
                .map((item, index) => setCurrItem(item, index));
            // Emit changes to items
            let enabledItems = mappedItems.filter((iItem) => iItem.enable && !iItem.complete);
            let completedItems = mappedItems.filter((iItem) => iItem.complete);
            if (this.enabledItemsLength !== enabledItems.length) {
                this.enabledItemsLength = enabledItems.length;
                this.$emit('update:enabledItemsLength', enabledItems.length);
            }
            if (!library.isEqualObj(this.enabledItems, enabledItems)) {
                this.enabledItems = enabledItems;
                this.$emit('update:enabledItems', mappedItems.filter((iItem) => iItem.enable && !iItem.complete));
            }
            if (this.completedItemsLength !== completedItems.length) {
                this.completedItemsLength = completedItems.length;
                this.$emit('update:completedItemsLength', completedItems.length);
            }
            if (!library.isEqualObj(this.completedItems, completedItems)) {
                this.completedItems = completedItems;
                this.$emit('update:completedItems', mappedItems.filter((iItem) => iItem.complete));
            }
            this.tmpMappedItems = mappedItems;
            return mappedItems;
        },
        visibleItems() {
            if (library.optimization.itemListitemListUseTmpComputedWhileSubscriptions) {
                if (this.componentPendingSubscriptions.length && this.tmpVisibleItems.length) return this.tmpVisibleItems;
            }
            let currNumber = 0;
            let visibleCount = this.visibleItemCount();
            let end = this.currentPage * visibleCount;
            let start = end - visibleCount;
            if (start < visibleCount) start = 0;
            let availableRecords = this.config.enableFilter ? this.filteredItems : this.mappedItems;
            this.availableRecords = availableRecords;
            let items = availableRecords.map((item) => {
                if (!item.excludeFromCount) {
                    currNumber++;
                    item.number = currNumber;
                }
                return item;
            });
            if (items.length && this.groupedBy) {
                const [perspective, prop] = this.groupedBy[0].split(':::');
                items = this.groupItems(items, prop);
            }
            this.itemsLength = items.length;
            if (items.length) this.initialLoadComplete = true;
            const visibleItems = items.slice(start, end);
            if (!library.isEqualObj(this.tmpVisibleItems, visibleItems)) {
                this.tmpVisibleItems = visibleItems;
                this.$emit('update:visibleItems', visibleItems);
            }
            if (this.visibleItemsLength !== visibleItems.length) {
                this.visibleItemsLength = visibleItems.length;
                this.$emit('update:visibleItemsLength', visibleItems.length);
            }
            // this.tmpVisibleItems = visibleItems;
            return visibleItems;
        },
        datasetRecords() {
            if (this.relativeRecords && this.relativeRecords.length) return false;
            // if relativeRecords was not passed through props continue
            if (!this.config.datasetIds) return false;
            // if there are datasetIds
            let outputRecords = [];
            this.config.datasetIds.forEach((payload) => {
                // This is here to handle legacy values
                // TODO remove this after all legacy configurations have been updated
                if (typeof payload === 'string') {
                    payload = { source: [payload] };
                }
                let parentRecords = false;
                // let sDsParentRecords = {};
                let prType = this.config.parentRecordType;
                if (prType) {
                    parentRecords = {};
                    if (prType === 'parentRecords') {
                        parentRecords[1] = this.parentRecords.vol;
                        parentRecords[2] = this.parentRecords.opp;
                        parentRecords[3] = this.parentRecords.org;
                    } else {
                        let types = {
                            volId: 1,
                            oppId: 2,
                            orgId: 3,
                            entryPointId: 34
                        };
                        parentRecords[types[prType]] = [this[prType]];
                        // sDsParentRecords[prType] = this[prType];
                    }
                }
                // Configure the payload
                let sourceItem = {
                    volId: this.volId,
                    oppId: this.oppId,
                    orgId: this.orgId,
                    entryPointId: this.entryPointId
                };
                // Map params to records and other contextual or static information
                let params = library.recordSourceMapper(payload.source[0], payload.params, sourceItem, this);
                // Subscribe to the data
                payload = {
                    ...payload,
                    params,
                    parentRecords
                };
                let subscription = this.recordStore.subscribe(payload, this.cId, false, false);
                let records = library.cloneObj(this.recordStore.getSubscription(subscription.key));
                // When all initialLoading is false set itemList initialLoadComplete to true (runs once)
                this.watchSubscription(subscription.key, {
                    watchProperty: 'initialLoading', // Also collectAs
                    startFnc: () => {
                        this.initialLoadComplete = false;
                    },
                    endFnc: () => {
                        if (!this.componentSubscriptions?.initialLoading.length) {
                            setTimeout(() => {
                                // Add a little delay to prevent message blips
                                // while the data is being processed through mappedItems and other computed
                                // If there is data, initialLoadComplete will be set before this timeout runs
                                this.initialLoadComplete = true;
                            }, 5000); // NOSONAR
                        }
                    }
                });
                if (!library.objIsEmpty(records)) {
                    let sortedRecordIds = subscription.recordIds.length
                        ? subscription.recordIds
                        : Object.keys(records);
                    if (Array.isArray(sortedRecordIds) && sortedRecordIds.length) {
                        sortedRecordIds.forEach((recordId) => {
                            outputRecords.push(records[recordId]);
                        });
                    }
                }
            });
            return outputRecords;
        },
        recordList() {
            let records = this.datasetRecords || this.relativeRecords;
            if (!Array.isArray(records) || !records.length) return [];
            return library.cloneObj(records);
        },
        headerStyles() {
            return this.config.headerStyles || {};
        },
        pages() {
            let pages = [];
            let visibleCount = this.visibleItemCount();
            let pageCount = Math.ceil(this.itemsLength / visibleCount);
            for (let i = 0; i < pageCount; i++) {
                let pageItem = {
                    text: 'Page ' + (i + 1),
                    value: i + 1
                };
                pages.push(pageItem);
            }
            return pages;
        },
        pageCount() {
            return this.pages.length;
        },
        startingItem() {
            let output = (this.currentPage * this.visibleItemCount()) - this.visibleItemCount();
            if (output < 1) output = 1;
            return output;
        },
        endingItem() {
            let output = this.currentPage * this.visibleItemCount();
            if (output < 0) output = this.visibleItemCount();
            return output;
        },
        groupedBy() {
            return this.config?.groupItemsBy?.length ? this.config?.groupItemsBy : false;
        },
        isDraggable() {
            return this.config?.allowDragAndDrop;
        }
    },
    methods: {
        filteredItemsUpdated() {
            this.currentPage = 1;
        },
        onDragstart(event, item) {
            event.dataTransfer.setData("application/json", JSON.stringify(item));
            event.dataTransfer.effectAllowed = "move";
        },
        onDragend(event, item) {
            this.dragIndex = false;
        },
        onDragover(event, item, lastItem) {
            event.preventDefault();
            event.target.classList.add('hovered');
            event.dataTransfer.dropEffect = "move";
        },
        onDragleave(event) {
            event.preventDefault();
            event.target.classList.remove('hovered');
        },
        onDrop(event, item, lastItem) {
            event.preventDefault();
            event.target.classList.remove('hovered');
            let movedItem = event.dataTransfer.getData("application/json");
            if (library.isJson(movedItem)) movedItem = JSON.parse(movedItem);
            let rItems = library.cloneObj(this.visibleItems);
            let spliceIndex = item.itemIndex < movedItem.itemIndex
                ? item.itemIndex
                : item.itemIndex - 1;
            if (lastItem) spliceIndex = rItems.length - 1;
            rItems.splice(movedItem.itemIndex, 1); // Remove the item from original index
            rItems.splice(spliceIndex, 0, movedItem); // Add the item back in at new index
            rItems = rItems.map((item) => item.itemIndex);
            this.$emit('update:itemOrder', { itemOrder: rItems });
            this.dragIndex = false;
        },
        expandGroup(group) {
            const shouldExpand = this?.config?.expandGroupDefault || group.itemRecordsCount > 1;
            if (shouldExpand) {
                this.expandedGroupItems[group.group] = !this.expandedGroupItems[group.group];
            }
        },
        // TODO: Are mouseEnter and mouseLeave still used?
        mouseEnter(item) {
            item.delayedHover = true;
            setTimeout(() => {
                if (item.delayedHover) item.hovered = true;
            }, 300);
        },
        mouseLeave(item) {
            item.delayedHover = false;
            item.hovered = false;
        },
        targetItem(index, item) {
            // Targeted items and selected items are not the same
            // There can be only one targeted item, the most recently clicked item
            this.targetedItemIndex = index;
            this.$emit('update:targetedItem', item, index);
        },
        // generateId(type, itemIndex = 0, propIndex = 0, notUnique = false) {
        //     const cId = this.cId.replace(" ", /_/g).toLowerCase();
        //     let templateId = `itemTemplate_${cId}_${itemIndex}_${propIndex}_${type}`;
        //     if (notUnique) return templateId;
        //     return `${templateId}_${library.getUniqueID()}`;
        // },
        // createComp(template, type, itemIndex, titleIndex) {
        //     let iTemplateCmpId = !this.config?.groupItemsBy ? this.generateId(type, itemIndex, titleIndex, true) : this.generateId(type, itemIndex, titleIndex);
        //     let iTemplate = `<div>${template}</div>`;
        //     let component = this;
        //     let recordStore = this.recordStore;
        //     vueApp.component(iTemplateCmpId, {
        //         template: iTemplate,
        //         data() {
        //             return {
        //                 templateData: {}, // A place to put custom, local, template specific settings
        //                 component: component,
        //             };
        //         },
        //         props: {
        //             hovered: {
        //                 type: Boolean,
        //                 default: false
        //             },
        //             targeted: {
        //                 type: Boolean,
        //                 default: false
        //             },
        //             record: {
        //                 type: Object,
        //                 default() {
        //                     return {};
        //                 }
        //             },
        //             itemRecords: {
        //                 type: Array,
        //                 default() {
        //                     return [];
        //                 }
        //             },
        //             index: {}
        //         },
        //         computed: {
        //             moment() {
        //                 return library.getMomentObj();
        //             },
        //             actionStore() {
        //                 return actionStore;
        //             },
        //             records() {
        //                 return recordStore.records;
        //             },
        //             // These will be available fields to use in the itemList headerTemplate
        //             // example: records.vol[volId].VOL_FIRST_NAME
        //             // example: records.user.CLIENT_NAME
        //             // example: records.opp[oppId]['CA#Application_Questions/Region_0']
        //             volId() {
        //                 return component.contextVolId;
        //             },
        //             user() {
        //                 return component.user;
        //             },
        //             oppId() {
        //                 return component.contextOppId || component.oppId;
        //             },
        //             orgId() {
        //                 return component.contextOrgId || component.orgId;
        //             },
        //             aeId() {
        //                 return component.contextAeId || component.aeId;
        //             },
        //             entryPointId() {
        //                 return component.contextEntryPointId || component.entryPointId;
        //             },
        //             affId() {
        //                 return component.contextAffId || component.affId;
        //             },
        //             schId() {
        //                 return component.contextSchId || component.schId;
        //             },
        //             lbeId() {
        //                 return component.contextLbeId || component.lbeId;
        //             }
        //         },
        //         methods: {
        //             trimHTML(str) {
        //                 return library.trimHTML(str);
        //             },
        //             dateDetails(dateStr, formatStr) {
        //                 let momentDate = library.getMoment(dateStr, formatStr);
        //                 return {
        //                     D: momentDate.format('D'),
        //                     MMM: momentDate.format('MMM'),
        //                     dddd: momentDate.format('dddd'),
        //                 };
        //             },
        //             toggleDelay(time, property) {
        //                 this.templateData[property] = !this.templateData[property];
        //                 setTimeout(() => {
        //                     this.templateData[property] = !this.templateData[property];
        //                 }, parseInt(time, 10));
        //             }
        //         }
        //     });
        //     return iTemplateCmpId;
        // },
        onPropsUpdate() {
            if (!this.itemList.length) return;
            this.itemList.forEach((cItem, itemIndex) => {
                if (!Array.isArray(cItem.titles)) cItem.titles = [];
                cItem.titles.forEach((cTitle, titleIndex) => {
                    if (!cTitle.tmpTemplateId) {
                        cTitle.tmpTemplateId = () => (createComp.call(this, cTitle.template, 'title', itemIndex, titleIndex));
                    }
                });
                if (!Array.isArray(cItem.descriptions)) cItem.descriptions = [];
                cItem.descriptions.forEach((cDescription, descIndex) => {
                    if (!cDescription.tmpTemplateId) {
                        cDescription.tmpTemplateId = () => (createComp.call(this, cDescription.template, 'description', itemIndex, descIndex));
                    }
                });
            });
        },
        async actionClick(event, control, actionParams) {
            const [action, item] = actionParams;
            let response;
            // Perform an action
            let actionConfig = library.cloneObj(action.action);
            let prevItem = this.visibleItems[item.itemIndex - 1];
            let nextItem = this.visibleItems[item.itemIndex + 1];
            if (Array.isArray(actionConfig)) {
                let controlParams = {
                    name: control.name,
                    label: control.label
                };
                if (actionParams && actionParams?.[0]?.ACTIVITY_ENTITY_ACTION_ID) {
                    controlParams.ACTIVITY_ENTITY_ACTION_ID = actionParams[0].ACTIVITY_ENTITY_ACTION_ID;
                }
                let tmpRecord = {
                    ...item.record, prevItem, nextItem, controlParams
                };
                if (!tmpRecord.VOLUNTEER_ID && this.contextVolId) {
                    tmpRecord.VOLUNTEER_ID = this.contextVolId;
                }
                response = await actionStore.runChainedActions(actionConfig, tmpRecord, this);
            }
            else {
                if (actionConfig.action) {
                    let payload = library.recordSourceMapper(actionConfig.action, actionConfig.params, { ...item.record, prevItem, nextItem }, this, true);
                    if (payload) response = await actionStore[actionConfig.action](payload);
                }
            }
            return response;
        },
        visibleItemCount() {
            return this.config.visibleItemCount || 100; // NOSONAR
        },
        groupItems(items, prop) {
            const grouped = items.reduce((acc, item) => {
                const key = item?.record?.[prop];
                if (!acc[key]) {
                    acc[key] = {
                        group: key,
                        items: [],
                        itemRecordsCount: 0,
                        itemRecords: [],
                        headerId: createComp.call(this, this.config.txtGroupHeading, 'group_header', 1, 1)
                    };
                }
                acc[key].items.push(item);
                acc[key].itemRecords.push(item.record);
                acc[key].itemRecordsCount++;
                acc[key].expandItem = !this.expandGroupValue ? acc[key].itemRecordsCount > 1 : this.expandGroupValue;
                return acc;
            }, {});

            return Object.values(grouped);
        },
        onLoad() {
            this.headerTemplate = createComp.call(this, this.config?.header, 'itemListHeader');
        },
        async beforePrintFunction() {
            return new Promise((resolve) => {
                let groupPrintSetting = this.config?.printExpandCollapseGroupedItems;
                if (this.groupedBy) {
                    this.prePrintExpandedGroupItems = library.cloneObj(this.expandedGroupItems);
                    if (groupPrintSetting === 'expand' || groupPrintSetting === 'collapse') {
                        this.visibleItems.forEach((vItem) => {
                            if (vItem.itemRecordsCount > 1) this.expandedGroupItems[vItem.group] = groupPrintSetting === 'expand';
                        });
                    }
                }
                setTimeout(() => {
                    resolve();
                }, 5000); // NO SONAR // TODO replace this wait for subscriptions
            });
        },
        afterPrintFunction() {
            for (let groupId in this.expandedGroupItems) {
                if (groupId === 'length') continue;
                this.expandedGroupItems[groupId] = !!this.prePrintExpandedGroupItems[groupId];
            }
        }
    },
    components: {
        condensedFilter: defineAsyncComponent(() => import('@common/condensedFilter/condensedFilter.vue')),
        'ejs-skeleton': SkeletonComponent
    }
};

componentStore.initComponent(itemListCmp, itemListConfig, '@common/itemList.vue');
export { itemListConfig, itemListCmp, editorSettings };
export default itemListCmp;
</script>

<style scoped>
.item-list-container {
    border: 1px solid #e0e0e0;
    background: #fff;
    padding: 5px;
    overflow: auto;
    position: relative;
}
.item-list-header {
    font-size: 20px;
    font-weight: bold;
    color: black;
    padding: 5px;
    border-bottom: 1px solid #e0e0e0;
}
.item-list-header, .item-list-footer {
    display: flex;
    flex-wrap: wrap;
}
.item-list-header .header-text {
    flex-grow: 9;
}
ul.item-list {
    margin-top: 0;
    padding-left: 0;
}
.list-item {
    display: block;
    overflow: hidden;
    position: relative;
}
.list-item-inner {
    display: flex;
    flex-direction: row;
}
.list-component-item .list-item-inner {
    display: block;
}
.list-item:last-child {
    border: none;
}
.list-item.dragged-item {
    background: #E9FBFF;
    border: 2px dashed #0a9bc0;
    overflow: visible;
    padding-left: 2px;
}
.group-container {
    margin: 0.5rem 0;
}
.group-expanded-container {
    border: 1px solid #e0e0e0;
    border-top: none;
    padding: 0.5rem;
}
.list-item .group_div {
    display: grid;
    grid-template-columns: 1.5rem auto;
    align-items: center;
}
.list-item.group-header {
    display: flex;
    align-items: center;
    font-weight: 500;
    padding: 1rem;
    cursor: pointer;
    border-bottom: 1px solid transparent;
}
.list-item.group-header i {
    display: flex;
    justify-content: center;
    align-items: center;
    font-size: 1rem;
    min-width: 15px;
}
.list-item.group-header .square-skeleton {
    margin-right: 1rem;
}
.list-item.skeleton {
    display: flex;
    align-items: center;
    justify-content: space-between;
}
.header-skeleton {
    margin: 1rem 2rem;
}
.button-skeleton {
    margin: 1.5rem 2rem;
}
.list-item:nth-child(even) {
    /* background: rgba(0,0,0, 0.045); */
}
.item-icon {
    border-radius: 50%;
    font-weight: bold;
    line-height: 33px;
    text-align: center;
    grid-area: icon;
}
.item-icon.icon-none {
    display: none;
}
.item-icon.icon-small {
    width: 32px;
    height: 32px;
    padding-top: 0;
    font-size: 18px;
}
.item-icon.icon-medium {
    width: 64px;
    height: 64px;
    font-size: 22px;
    padding-top: 8px;
}
.item-icon.icon-large {
    width: 96px;
    height: 96px;
    padding-top: 12px;
    font-size: 32px;
}
.item-icon.icon-large.icon-avatar, .item-icon.icon-medium.icon-avatar {
    padding-top: 0;
}
.item-icon.icon-number {
    background: #e3f1fc;
}
.item-title {
    z-index: 3;
}
.item-content {
    flex-grow: 9;
    grid-area: content;
    padding: 0 10px 0 10px;
}
.item-content p {
    margin: 6px 0;
    font-size: 0.85rem;
}
.action-ctrls {
    grid-area: action;
}
.complete-indicator {
    grid-area: complete
}
.item-action-area {
    align-items: center;
}
.item-action-item {
    text-align: right;
    margin: 5px 0 5px 10px;
}
.item-action-item button {
    display: flex;
}
.item-complete {
    font-size: 16px;
    background: #c8e5f8;
}
.item-disabled {
    position: relative;
    font-size: 14px;
    background: #ddd;
}
.item-disabled.list-item:nth-child(even) {
    background: #ddd;
}
.item-disabled::after {
    content: "";
    display: block;
    width: 100%;
    height: 100%;
    top: 0;
    left: 0;
    position: absolute;
    background: white;
    cursor: not-allowed;
    opacity: 0.25;
}
.action-btn-inline {
    display: flex;
    align-items: flex-end;
}
/*.action-icon.e-icons {
font-size: 1.25rem;
}*/
:deep(.e-icons.e-complete:before) {
    content: '\e192' !important;
}
.e-icons.e-edit:before {
    content: '\e944' !important;
}
.e-icons.e-email:before {
    content: '\e816' !important;
}
.e-icons.e-eye:before {
    content: '\e345' !important;
}
.e-icons.e-mouse-pointer:before {
    content: '\ec1d' !important;
}
.e-icons.e-circle-info:before {
    content: '\e607' !important;
}
.e-icons.e-delete-5:before {
    content: '\e706' !important;
}
.list-pagination {
    text-align: right;
    margin: 0.5rem 0 0.25rem;
}
.list-pagination > div {
    display: flex;
    align-items: center;
    justify-self: flex-end;
    width: fit-content;
    padding: 3px 8px 3px 8px;
    line-height: 0;
    border-radius: 5px;
    background: rgba(255, 255, 255, .7);
    border: 1px solid rgba(0,0,0, 0.3);
    box-shadow: 1px 1px 10px rgb(0 0 0 / 10%);
}
.list-pagination .current-page {
    display: inline-block;
    margin-left: 1.5rem;
}
.list-pagination .total-page-count{
    display: block;
    font-size: 14px;
    color: #444;
    font-family: sans-serif;
}
.list-pagination :deep(.e-input-group, .e-input-group.e-control-wrapper) {
    margin-bottom: 0;
}
/* Drag & Drop Classes */
.drag-drop-handle {
    width: 1.6rem;
    height: auto;
    margin: 10px 0;
    color: #676767;
    cursor: grab;
    position: absolute;
    z-index: 1000;
    left: 0;
    top: 0;
    bottom: 0;
    display: flex;
    flex-direction: column;
    justify-content: center;
    text-align: center;
    border-right: 1px solid rgba(0,0,0, 0.1);
    opacity: 0.67;
    transition: opacity .3s ease-in-out;
    border-radius: 2px;
}
.drag-drop-handle:hover {
    border-right: 1px solid rgba(0,0,0, 0.25);
    color: #10748c;
    box-shadow: -2px 0 4px -2px inset rgb(255 255 255 / 70%);
    opacity: 1;
}
.dragged-item .drag-drop-handle {
    cursor: grabbing;
}
.item-draggable .item-content {
    margin-left: 2rem;
}
.list-item:hover .e-drag-and-drop::before {
    content: "\e903" !important;
    font-family: "e-icons" !important;
}
.drop-zone {
    position: absolute;
    width: 100%;
    height: 40px;
    padding-top: 18px;
    left: 0;
    top: -20px;
    display: none;
    z-index: 1000;
    background: rgba(255,255,255, 0.3);
}
.drop-zone.last-drop-zone {
    height: 20px;
    top: auto;
    left: 0;
    bottom: 0;
}
.drop-indicator {
    height: 4px;
    background: #3495ad;
    position: relative;
    z-index: 1000;
    width: 102%;
    border-radius: 0 2px 2px 0;
    display: none;
    pointer-events: none; /* used to prevent dragleave when over indicator */
}
/* :hover pseudo class does not work when holding mouse click down */
.drop-zone.hovered .drop-indicator {
    display: block;
}
.drop-indicator:before {
    display: inline-block;
    content: ' ';
    width: 0;
    height: 0;
    position: relative;
    left: -5px;
    top: -6px;
    border-top: 7px solid transparent;
    border-bottom: 7px solid transparent;
    border-left: 12px solid #3495ad;
}
.is-dragging .drop-zone {
    display: block;
}
/* Prevent display of the drop-zone of the item being dragged, and it's next sibling's drop-zone */
.is-dragging .dragged-item .drop-zone, .is-dragging .dragged-item + .list-item .drop-zone:not(.last-drop-zone) {
    display: none;
}
.is-dragging .list-item {
    overflow: inherit;
}
.item-list-container.is-dragging {
    position: relative !important;
    overflow: inherit;
    overflow-x: visible !important;
}

.no-records_msg {
    background: #eee;
    border: 1px solid #d3d3d3;
    border-radius: 4px;
    margin: 1rem;
    padding: 1rem 2.5rem;
}

.fade-enter-active,
.fade-leave-active {
    transition: opacity 300ms ease;
}
.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}
.fade-enter-to,
.fade-leave-from {
    opacity: 1;
}
.grouped-item.list-item {
    opacity: 0;
    transform: translateX(4rem);
    animation: fadeIn 250ms forwards;
}
@keyframes fadeIn {
    to {
        transform: translateX(0);
        opacity: 1;
    }
}
@media (max-width: 650px) {
    .list-item {
        display: grid;
        grid-template-columns: auto 1fr auto;
        grid-template-rows: 1fr auto;
        grid-template-areas:
    "icon content complete"
    ". action .";
    }
    .action-ctrls {
        justify-self: start;
    }
    .complete-indicator {
        align-self: start;
    }
}
</style>
